import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Upload, Image } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const Settings = () => {
  const { user } = useAuth();
  const [logoUrl, setLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState('');

  useEffect(() => {
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      const res = await api.get('/api/settings/app-logo');
      setLogoUrl(res.data.url || '');
      setPreview(res.data.url || '');
    } catch {}
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('File too large (max 2MB)'); return; }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await api.post('/api/settings/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        setLogoUrl(res.data.url);
        toast.success('Logo uploaded! Apps will show new logo on next launch.');
      }
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '600px' }}>
      <h2 style={{ margin: '0 0 24px', fontSize: '22px' }}>Settings</h2>

      {/* App Logo - Superadmin only */}
      {user?.role === 'superadmin' && <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Image size={18} /> App Logo
        </h3>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
          Upload your app logo. It will appear on the splash screen of both Mobile and TV apps automatically.
          Recommended: PNG with transparent background, min 512x512px.
        </p>

        {/* Preview */}
        <div style={{ width: '200px', height: '200px', background: '#000', borderRadius: '12px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', overflow: 'hidden' }}>
          {preview ? (
            <img src={preview} alt="App Logo" style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }} />
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
              <Image size={48} style={{ opacity: 0.3, marginBottom: '8px' }} />
              <div style={{ fontSize: '12px' }}>No logo set</div>
            </div>
          )}
        </div>

        {logoUrl && (
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px', wordBreak: 'break-all' }}>
            Current: <a href={logoUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{logoUrl}</a>
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <label style={{ cursor: 'pointer' }}>
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} disabled={uploading} />
            <div className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', pointerEvents: uploading ? 'none' : 'auto', opacity: uploading ? 0.7 : 1 }}>
              <Upload size={16} />
              {uploading ? 'Uploading...' : 'Upload Logo'}
            </div>
          </label>
          {logoUrl && (
            <button className="btn btn-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              onClick={async () => {
                try {
                  await api.post('/api/settings/upload-logo/remove');
                  setLogoUrl(''); setPreview('');
                  toast.success('Logo removed');
                } catch { toast.error('Failed to remove'); }
              }}>
              Remove Logo
            </button>
          )}
        </div>
      </div>}

      {/* App Versions */}
      <div className="card" style={{ padding: '24px' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>App Versions</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: '14px' }}>📱 Mobile App</span>
            <span style={{ fontSize: '14px', color: 'var(--accent)', fontWeight: '600' }}>v1.3.8</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: '14px' }}>📺 TV App</span>
            <span style={{ fontSize: '14px', color: 'var(--accent)', fontWeight: '600' }}>v1.0.5</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
